import { type JSX } from 'react';

import { cn } from '@/lib/utils';

/**
 * Stepper — generic numbered-circle progress indicator shared by the
 * multi-step wizards (campaign create, contacts import, Press-1 Basic).
 *
 * Extracted verbatim from the copy that was inlined per-page. Callers
 * pass already-resolved step labels (run them through i18n first) and a
 * 1-indexed `current` step. Past steps render filled volt with a check,
 * the current step filled volt with its number, future steps outlined.
 */
export interface StepperProps {
  /** Ordered, already-translated step labels. */
  steps: string[];
  /** 1-indexed current step. */
  current: number;
}

export function Stepper({ steps, current }: StepperProps): JSX.Element {
  return (
    <ol className="flex items-center gap-2">
      {steps.map((label, i) => {
        const n = i + 1;
        const done = current > n;
        const active = current === n;
        return (
          <li key={n} className="flex items-center gap-2 flex-1 last:flex-none">
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <span
                className={cn(
                  'w-7 h-7 rounded-full grid place-items-center text-[12px] font-semibold',
                  done && 'bg-volt text-volt-ink',
                  active && 'bg-volt text-volt-ink',
                  !done && !active && 'bg-surface-3 text-text-3 border border-line',
                )}
              >
                {done ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  n
                )}
              </span>
              <span
                className={cn(
                  'text-[13px]',
                  active && 'text-text font-medium',
                  done && 'text-text-2',
                  !done && !active && 'text-text-3',
                )}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={cn('flex-1 h-px ml-1', done ? 'bg-volt' : 'bg-line')}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
